import * as React from 'react';
import { useState, useEffect, useCallback, useRef, useReducer } from 'react';
import * as Fuse from 'fuse.js';
import cx from 'classnames';
import _ from 'lodash';

import TextField from 'material-ui/TextField';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';
import IconMenu from 'material-ui/IconMenu';
import IconButton from 'material-ui/IconButton';

import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer';
import { List } from 'react-virtualized/dist/es/List';
import { ScrollSync } from 'react-virtualized/dist/es/ScrollSync';

import EditIcon from 'material-ui/svg-icons/image/edit';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import FileDownload from 'material-ui/svg-icons/file/file-download';
import DeleteSweep from 'material-ui/svg-icons/content/delete-sweep';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';

import AwaitableBtn from 'components/Base/AwaitableBtn';

import { 
    loadDefFile, 
    loadLstFile, 
    buildLst, 
    getEmptyFeatures, 
    getColumns, 
    saveDef, 
    validator, 
    removeDuplicates, 
    calculateDuplicateEntries, 
} from './service';

import * as gazetteerService from 'services/gazetters';

import helpers from 'helpers/index';
import { execute, UserPerms } from 'store/configureStore';

import './styles.scss';
import { withGazetterContentSave } from './HOC';
import { showNotification } from 'helpers';

class DynamicKey {
    KEY = Symbol('dynamic key');

    /**
     * @private
     */
    value = 1;

    get next() { this.value += 1; return this.value; }
}


const GazetteerEditorPage = ({
    saveContentMutation,
    isSavingContent,
    isSaveContentSuccess,
}) => {
    const [defData, setDefData] = useState([]);
    const [defView, setDefView] = useState([]);
    const [defVersion, setDefVersion] = useState(undefined);
    const [selectedLst, setSelectedLst] = useState(undefined);
    const [defIndex, setDefIndex] = useState(new Map());
    const lstData = useRef();
    const lstView = useRef();
    const [lstIndex, setLstIndex] = useState(new Map());
    const [lstDuplicatesCount, setLstDuplicatesCount] = useState(0);
    const [selectedDefKey, setSelectedDefKey] = useState(undefined);
    const [selectedLstVersion, setSelectedLstVersion] = useState(undefined);
    
    const columns = useRef(new Set());
    const lstLocalVersion = useRef(0);
    const isNewFeatureNameValid = useRef(true);

    const dKey = useRef(new DynamicKey()).current;
    const defList = useRef(null);
    const lstList = useRef(null);
    const featureNameRef = useRef(null);
    const invalidDefRows = useRef(new Set());
    const invalidRows = useRef(new Set());
    const originalLstData = useRef([]);
    const originalLstIndex = useRef(new Map());
    const gazetteersChanged = useRef([]);
    const originalColumns = useRef(new Set());

    // hook to replicate this.forceUpdate() to preserve the original component's logic
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const prepareData = useCallback((items) => {
        const map = new Map();
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item[dKey.KEY]) {
                item[dKey.KEY] = dKey.next;
            }
            map.set(item[dKey.KEY], item);
        }
        return map;
    }, [dKey]);

    useEffect(() => {
        const init = async () => {
            const [, data] = await execute(loadDefFile);
            setDefIndex(prepareData(data));
            setDefData(data);
            setDefView([...data]);
        };
        init();
        return () => {
            gazetteersChanged.current = [];
        };
    }, [prepareData]);


    const recalculateRowChanges = useCallback((indexOverride) => {
        const currentChanges = gazetteersChanged.current;
        const renames = currentChanges.filter(c => c.status === 'renameFeature');
        const newRowChanges = [];

        // 1. calculate 'new' and 'replace' changes by iterating current data
        lstData.current.forEach((currentItem) => {
            const key = currentItem[dKey.KEY];
            const originalItem = originalLstIndex.current.get(key);

            if (!originalItem) { // this is a new row
                const features = _.cloneDeep(currentItem);
                delete features[dKey.KEY];
                delete features.$value;
                newRowChanges.push({
                    key, status: 'new', value: currentItem.$value, lastFeatures: features, 
                });
                return;
            }

            // for existing rows, "un-project" the current item to compare with the original
            const unprojectedCurrent = _.cloneDeep(currentItem);
            [...renames].reverse().forEach((rename) => {
                if (unprojectedCurrent.hasOwnProperty(rename.value)) {
                    unprojectedCurrent[rename.oldValue] = unprojectedCurrent[rename.value];
                    delete unprojectedCurrent[rename.value];
                }
            });

            const allKeys = new Set([...Object.keys(originalItem), ...Object.keys(unprojectedCurrent)]);
            const normalizedOriginal = {};
            const normalizedCurrent = {};

            allKeys.forEach((key) => {
                const originalValue = originalItem[key];
                const currentValue = unprojectedCurrent[key];
                const isNewKey = !originalItem.hasOwnProperty(key);

                if (isNewKey && (currentValue === null || currentValue === undefined || currentValue === '')) {
                    return;
                }
                normalizedOriginal[key] = (originalValue === null || originalValue === undefined) ? '' : originalValue;
                normalizedCurrent[key] = (currentValue === null || currentValue === undefined) ? '' : currentValue;
            });

            if (!_.isEqual(normalizedCurrent, normalizedOriginal)) {
                const features = {};
                columns.current.forEach((colName) => {
                    if (currentItem.hasOwnProperty(colName)) {
                        features[colName] = currentItem[colName];
                    }
                });
                if (currentItem.$value === originalItem.$value) {
                    newRowChanges.push({ 
                        key, status: 'replace', value: currentItem.$value, lastFeatures: features, 
                    });
                } else {
                    newRowChanges.push({ 
                        key, status: 'replace', value: currentItem.$value, oldValue: originalItem.$value, lastFeatures: features, 
                    });
                }
            }
        });

        // 2. calculate 'remove' changes by iterating original data
        originalLstData.current.forEach((originalItem) => {
            const key = originalItem[dKey.KEY];
            const indexToUse = indexOverride || lstIndex;
            if (!indexToUse.get(key)) {
                newRowChanges.push({ key, status: 'remove', value: originalItem.$value });
            }
        });

        // 3. combine rename records with the newly calculated row changes
        gazetteersChanged.current = [...renames, ...newRowChanges];
        forceUpdate();
    }, [dKey.KEY, lstData.current, lstIndex]);


    const _updateGazetteerChanges = useCallback(({ type, payload }) => {
        const newChanges = gazetteersChanged.current;
        
        if (type === 'FEATURE_RENAMED') {
            const { oldName, newName } = payload;

            newChanges.forEach((change) => {
                if (change.lastFeatures && change.lastFeatures.hasOwnProperty(oldName)) {
                    change.lastFeatures[newName] = change.lastFeatures[oldName];
                    delete change.lastFeatures[oldName];
                }
            });
            
            const existingRenameIndex = newChanges.findIndex(c => c.status === 'renameFeature' && c.value === oldName);

            if (existingRenameIndex > -1) {
                const existingRename = newChanges[existingRenameIndex];
                if (existingRename.oldValue === newName) {
                    newChanges.splice(existingRenameIndex, 1);
                } else {
                    existingRename.value = newName;
                }
            } else if (oldName !== newName) {
                newChanges.push({ status: 'renameFeature', value: newName, oldValue: oldName });
            }
        }
    }, []);
    
    const onDownloadLst = useCallback(() => {
        const content = buildLst(lstData.current, columns.current);
        const url = window.URL.createObjectURL(new window.Blob([content], { type: 'text/plain' }));
        const element = document.createElement('a');
        element.setAttribute('href', url);
        element.setAttribute('download', selectedLst);
        element.click();
        window.URL.revokeObjectURL(url);
    }, [selectedLst]);


    useEffect(() => {
        if (isSaveContentSuccess) {
            originalLstData.current = _.cloneDeep(lstData.current);
            originalColumns.current = new Set(columns.current);
            originalLstIndex.current = prepareData(originalLstData.current);
            gazetteersChanged.current = [];
            forceUpdate();
        }
    }, [isSaveContentSuccess, prepareData]);

    const onSaveLst = useCallback(async () => {
        if (!gazetteersChanged.current.length) return;

        const emptyFeatures = getEmptyFeatures(lstData.current, columns.current);
        if (emptyFeatures.length > 0) {
            showNotification(
                `Gazetteer ${selectedLst} has empty features: ${emptyFeatures.join(', ')}. \nRemove them or fill with at least one value per feature.`,
                'error',
            );
            return;
        }

        await saveContentMutation({
            gazetterName: selectedLst,
            values: gazetteersChanged.current,
        });
    }, [selectedLst, saveContentMutation]);

    const onSaveDef = useCallback(async () => {
        if (invalidDefRows.current.size > 0) {
            showNotification(
                `File has ${invalidDefRows.current.size} error(s)! Fix them before saving def.`,
                'error',
            );
            return;
        }

        const [_newVersion, data] = await execute(saveDef, defVersion, defData);
        
        setDefIndex(prepareData(data));
        setDefData(data);
        setDefView([...data]);
        setDefVersion(_newVersion);
    }, [defData, defVersion, prepareData]);

    const loadLst = useCallback(async (defEntry) => {
        gazetteersChanged.current = [];
        invalidRows.current.clear();
        
        let version;
        let items = [{ [dKey.KEY]: dKey.next }];
        try {
            [version, items] = await execute(loadLstFile, defEntry.fileName);
        } catch (err) {
            // file not found
        }

        const liveIndex = prepareData(items);

        originalLstData.current = _.cloneDeep(items);
        originalLstIndex.current = prepareData(originalLstData.current);
        
        const duplicatesCount = calculateDuplicateEntries(items);
        
        const newColumns = getColumns(items);
        newColumns.delete('$value');
        columns.current = newColumns;
        originalColumns.current = new Set(columns.current);

        lstLocalVersion.current = 0;
        lstData.current = items;
        lstView.current = [...items];
        setLstIndex(liveIndex);
        setLstDuplicatesCount(duplicatesCount);
        setSelectedDefKey(defEntry[dKey.KEY]);
        setSelectedLstVersion(version);
        setSelectedLst(defEntry.fileName);
        gazetteersChanged.current = [];
    }, [dKey, prepareData]);

    const onDedupLst = useCallback(async () => {
        await execute(removeDuplicates, selectedLst);
        const defEntry = defIndex.get(selectedDefKey);
        if (defEntry) {
            await loadLst(defEntry);
            gazetteersChanged.current = [];
        }
    }, [defIndex, selectedDefKey, selectedLst, loadLst]);

    const getValKey = useCallback((field, key) => `${field}:${key}`, []);

    const addInvalidMark = useCallback((invRows, field = '', key = '') => {
        const value = getValKey(field, key);
        invRows.add(value);
    }, [getValKey]);

    const removeInvalidMark = useCallback((invRows, field = '', key = '') => {
        const escapedField = helpers.escapeRegExp(field);
        const escapedKey = helpers.escapeRegExp(key);
        const keyReg = `${escapedField}:${escapedKey}`;
        invRows.forEach((invalidRow) => {
            if (invalidRow.match(keyReg)) {
                invRows.delete(invalidRow);
            }
        });
    }, []);

    const onDefCellChanged = useCallback((e, value) => {
        const el = e.target;
        if (value === undefined) value = el.value;
        const field = el.dataset.field;
        const index = parseInt(el.closest('[data-key]').dataset.key);

        if (validator.defValue(value)) {
            el.classList.remove('invalid');
            removeInvalidMark(invalidDefRows.current, field, index.toString());
        } else {
            el.classList.add('invalid');
            addInvalidMark(invalidDefRows.current, field, index.toString());
        }

        const item = defIndex.get(index);
        if (item) {
            item[field] = value;
        }
    }, [defIndex, addInvalidMark, removeInvalidMark]);

    const onLstCellChanged = useCallback((e, value) => {
        const el = e.target;
        if (value === undefined) value = el.value;
        const field = el.dataset.field;
        const key = parseInt(el.closest('[data-key]').dataset.key);

        const validatorFn = field === '$value' ? validator.lstValue : validator.lstFeature;
        if (validatorFn(value)) {
            el.classList.remove('invalid');
            removeInvalidMark(invalidRows.current, field, key.toString());
        } else {
            el.classList.add('invalid');
            addInvalidMark(invalidRows.current, field, key.toString());
        }

        const item = lstIndex.get(key);
        if (item) {
            item[field] = value;
            // no need to setLstData, just trigger recalculation
            recalculateRowChanges();
        }
    }, [lstIndex, recalculateRowChanges, addInvalidMark, removeInvalidMark]);

    const onLoadLst = useCallback(async (e) => {
        const el = e.target;
        const key = parseInt(el.closest('[data-key]').dataset.key);
        const defEntry = defIndex.get(key);
        if (defEntry) {
            await loadLst(defEntry);
        }
    }, [defIndex, loadLst]);

    const onAddFeature = useCallback(() => {
        const textEdit = featureNameRef.current;
        if (columns.current && textEdit) {
            const featureName = textEdit.getValue().trim();

            isNewFeatureNameValid.current = validator.lstFeature(featureName);
            if (!isNewFeatureNameValid.current || featureName === '') {
                forceUpdate();
                return;
            }

            textEdit.input.value = '';
            if (!featureName) {
                return alert('You should write valid feature name first');
            }
            columns.current.add(featureName);
            lstLocalVersion.current++;
            forceUpdate();
        }
    }, []);

    const onAddLstEntry = useCallback(() => {
        const newRow = { [dKey.KEY]: dKey.next, $value: '' };
        lstData.current.push(newRow);
        lstView.current = [...lstData.current];
        const newIndex = prepareData(lstData.current);
        setLstIndex(newIndex);
        
        recalculateRowChanges(newIndex);

        setTimeout(() => {
            if (lstList.current) {
                lstList.current.scrollToRow(lstData.current.length);
            }
        });
    }, [dKey, prepareData, recalculateRowChanges]);

    const onDeleteLstEntry = useCallback((e) => {
        const el = e.target;
        const key = parseInt(el.closest('[data-key]').dataset.key);

        removeInvalidMark(invalidRows.current, '', key.toString());

        const itemToRemove = lstIndex.get(key);
        lstData.current = lstData.current.filter(item => item !== itemToRemove);
        lstView.current = lstData.current.filter(item => item !== itemToRemove);
        const newIndex = prepareData(lstData.current);
        setLstIndex(newIndex);
        
        lstLocalVersion.current++;
        recalculateRowChanges(newIndex);
    }, [prepareData, removeInvalidMark, recalculateRowChanges]);

    const onAddDefRow = useCallback(() => {
        const row = {
            [dKey.KEY]: dKey.next, fileName: '', majorType: '', minorType: '', language: '', local: true, 
        };
        defData.push(row);
        defIndex.set(row[dKey.KEY], row);
        setDefView([...defData]);
        forceUpdate();
        setTimeout(() => {
            if (defList.current) {
                defList.current.scrollBy(0, Number.MAX_SAFE_INTEGER);
            }
        });
    }, [dKey, defData, defIndex]);

    const onDeleteDefEntry = useCallback((e) => {
        const el = e.target;
        const index = parseInt(el.closest('[data-key]').dataset.key);
        const itemToDelete = defIndex.get(index);
        if (!confirm(`Are you sure to remove ${itemToDelete.fileName}`)) {
            return;
        }
        const newDefData = defData.filter(item => item !== itemToDelete);
        setDefData(newDefData);
        setDefView([...newDefData]);
        defIndex.delete(index);
        forceUpdate();
    }, [defData, defIndex]);

    const updateInvalidMark = useCallback((invRows, oldField, newField) => {
        const escaped = helpers.escapeRegExp(oldField);
        const keyReg = `${escaped}:([^:]+)`;
        invRows.forEach((invalidRow) => {
            const match = invalidRow.match(keyReg);
            if (match) {
                invRows.delete(invalidRow);
                invRows.add(getValKey(newField, match[1]));
            }
        });
    }, [getValKey]);

    const onLstFeatureChanged = useCallback((oldValue, newValue, remove = false) => {
        if (remove) {
            if (!originalColumns.current.has(oldValue)) { // new feature being deleted
                // 1. remove the feature from all existing change records
                gazetteersChanged.current.forEach((change) => {
                    if (change.lastFeatures && change.lastFeatures.hasOwnProperty(oldValue)) {
                        delete change.lastFeatures[oldValue];
                    }
                });

                // 2. remove the column from the live data model
                columns.current.delete(oldValue);
                lstData.current.forEach((item) => {
                    delete item[oldValue];
                });

                // 3. re-evaluate all changes to see if any are now void
                const stillChanged = gazetteersChanged.current.filter((change) => {
                    if (change.status === 'remove' || change.status === 'renameFeature') {
                        return true;
                    }
                    const originalItem = originalLstIndex.current.get(change.key);
                    const currentItem = lstData.current.find(item => item[dKey.KEY] === change.key);
                    
                    if (originalItem && currentItem) {
                        const cleanCurrent = _.cloneDeep(currentItem);
                        delete cleanCurrent[dKey.KEY];
                        delete cleanCurrent.$value;
                        
                        const cleanOriginal = _.cloneDeep(originalItem);
                        delete cleanOriginal[dKey.KEY];
                        delete cleanOriginal.$value;
                        
                        return !_.isEqual(cleanCurrent, cleanOriginal);
                    }
                    return change.status === 'new' && currentItem;
                });
                gazetteersChanged.current = stillChanged;
            } else {
                // this is an original feature -> remove it visually
                columns.current.delete(oldValue);
                gazetteersChanged.current.push({ status: 'deleteFeature', value: oldValue });
            }

            lstLocalVersion.current++;
            removeInvalidMark(invalidRows.current, oldValue);
            forceUpdate();
            return;
        }

        if (!newValue || !columns.current.has(oldValue) || columns.current.has(newValue)) {
            forceUpdate();
            return;
        }

        // 1. mutate the live data structures FIRST
        updateInvalidMark(invalidRows.current, oldValue, newValue);
        const cols = Array.from(columns.current);
        const index = cols.indexOf(oldValue);
        cols[index] = newValue;
        columns.current = new Set(cols);

        lstData.current.forEach((item) => {
            item[newValue] = item[oldValue];
            delete item[oldValue];
        });

        // 2. update the rename records based on the action
        const isOriginalFeature = originalColumns.current.has(oldValue);
        const isPreviouslyRenamedFeature = gazetteersChanged.current.some(c => c.status === 'renameFeature' && c.value === oldValue);

        if (isOriginalFeature || isPreviouslyRenamedFeature) {
            _updateGazetteerChanges({ type: 'FEATURE_RENAMED', payload: { oldName: oldValue, newName: newValue } });
        }
        
        // 3. with all data consistent, recalculate all row changes
        recalculateRowChanges();
        
        // 4. finally, trigger the re-render
        lstLocalVersion.current++;
        forceUpdate();
    }, [recalculateRowChanges, lstData.current, removeInvalidMark, updateInvalidMark, dKey.KEY, _updateGazetteerChanges]);

    const onFilterDef = useCallback(_.debounce((e, text) => {
        if (text.length === 0) {
            setDefView(defData);
            return;
        }
        const fuse = new Fuse(defData, {
            keys: ['fileName', 'majorType', 'minorType'],
            shouldSort: false,
            threshold: 0.1,
            distance: 320,
            maxPatternLength: 32,
        });
        const items = fuse.search(text);
        setDefView(items);
    }, 150, { leading: true }), [defData]);

    const find_custom = useCallback((items, fields, text) => {
        const pattern = new RegExp(_.escapeRegExp(text), 'i');
        return items.filter(i => fields.some(f => pattern.test(i[f] || '')));
    }, []);

    const onFilterLst = useCallback(_.debounce((e, text) => {
        if (text.length === 0) {
            lstView.current = lstData.current;
            lstLocalVersion.current++;
            forceUpdate();
            return;
        }
        const items = find_custom(lstData.current, ['$value', ...Array.from(columns.current, col => col)], text);
        lstView.current = items;
        lstLocalVersion.current++;
        forceUpdate();
    }, [find_custom]), []);

    const renderDefRow = useCallback((item) => {
        const key = item[dKey.KEY];
        return (
            <tr key={key} data-key={key} className={cx({ loaded: key === selectedDefKey })}>
                <td className="cell readonly">
                    <input disabled={!item.local} defaultValue={item.fileName} data-field="fileName" onChange={onDefCellChanged} />
                </td>
                <td className="cell">
                    <input defaultValue={item.majorType} data-field="majorType" onChange={onDefCellChanged} />
                </td>
                <td className="cell">
                    <input defaultValue={item.minorType} data-field="minorType" onChange={onDefCellChanged} />
                </td>
                <td>
                    <select data-field="$target" defaultValue={item.$target} onChange={onDefCellChanged}>
                        <option value="common">common</option>
                        <option value="cargo">cargo</option>
                        <option value="vessel">vessel</option>
                    </select>
                </td>
                <td className="controls">
                    <EditIcon className="edit" onClick={onLoadLst} />
                    <DeleteIcon className="delete" onClick={onDeleteDefEntry} />
                </td>
            </tr>
        );
    }, [selectedDefKey, onDefCellChanged, onLoadLst, onDeleteDefEntry, dKey.KEY]);

    const renderDefTable = useCallback(() => {
        const disableAdminFunctions = !UserPerms.isAdmin();
        return (
            <React.Fragment>
                <div className='paper-block margin-10'>
                    <span className='name-block absolute small-name'>Manage gazetteer index file</span>
                    <RaisedButton label="Add row" primary onClick={onAddDefRow} />
                    <AwaitableBtn><RaisedButton label="Save" primary onClick={onSaveDef} /></AwaitableBtn>
                    <div className="right">
                        <AwaitableBtn>
                            <RaisedButton label='Sync lst' disabled={disableAdminFunctions} onClick={gazetteerService.saveToStaging} primary />
                        </AwaitableBtn>
                        <AwaitableBtn>
                            <RaisedButton label='Deploy' disabled={disableAdminFunctions} onClick={gazetteerService.saveToProduction} secondary />
                        </AwaitableBtn>
                    </div>
                </div>
                <div><TextField placeholder="Search here..." fullWidth onChange={onFilterDef} /></div>
                <div ref={defList} className="def-list">
                    <table>
                        <thead><tr><th>File</th><th>Major type</th><th>Minor type</th><th>Type</th></tr></thead>
                        <tbody>{defView.map(renderDefRow)}</tbody>
                    </table>
                </div>
            </React.Fragment>
        );
    }, [defView, renderDefRow, onAddDefRow, onSaveDef, onFilterDef]);
    
    const renderLstFeaturesRow = useCallback(({ index, style }) => {
        const item = lstView.current[index];
        const key = item[dKey.KEY];
        return (
            <div 
                key={`${key}${selectedLst}${lstLocalVersion.current}`} 
                data-key={key} 
                style={{ ...style, display: 'flex', width: 'auto' }}
            >
                {Array.from(columns.current, field => (
                    <div key={key + field} className="cell">
                        <input 
                            style={{ height: style.height }} 
                            className={cx({ invalid: invalidRows.current.has(getValKey(field, key)) })} 
                            defaultValue={item[field]} 
                            data-field={field} 
                            onChange={onLstCellChanged} 
                        />
                    </div>
                ))}
            </div>
        );
    }, [selectedLst, selectedLstVersion, onLstCellChanged, dKey.KEY, getValKey]);

    const renderLstTable = useCallback(() => {
        if (!lstView.current) {
            return <div className="empty"><span>Select .lst from left list to edit</span></div>;
        }

        prettyPrint(gazetteersChanged);
        return (
            <React.Fragment>
                <div className='paper-block margin-10'>
                    <span className='name-block absolute small-name'>Manage gazetteer file</span>
                    <div className="with-input">
                        <TextField 
                            ref={featureNameRef} 
                            defaultValue="" 
                            errorText={!isNewFeatureNameValid.current && 'invalid feature name'} 
                            style={{ height: 35 }} 
                            underlineStyle={{ bottom: 0 }} 
                            underlineFocusStyle={{ bottom: 0 }} 
                        />
                        <RaisedButton label="Add feature" primary onClick={onAddFeature} />
                    </div>
                    <RaisedButton label="Add row" primary onClick={onAddLstEntry} />
                    <RaisedButton 
                        label={isSavingContent ? 'Saving...' : 'Save'}
                        primary 
                        onClick={onSaveLst} 
                        disabled={isSavingContent}
                    />
                    <div className="statuses">{lstDuplicatesCount > 0 && <div className="dups">{lstDuplicatesCount} duplicate(s)</div>}</div>
                    <IconMenu
                        iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
                        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                        targetOrigin={{ horizontal: 'left', vertical: 'top' }}
                        className="additional-fns"
                    >
                        <MenuItem primaryText="Download" leftIcon={<FileDownload />} onClick={onDownloadLst} />
                        <MenuItem primaryText="Dedup" leftIcon={<DeleteSweep />} onClick={onDedupLst} />
                    </IconMenu>
                </div>
                <div>
                    <TextField 
                        placeholder="Search here..." 
                        fullWidth 
                        onChange={onFilterLst} 
                        onKeyPress={(e) => { if (e.charCode === 13) onFilterLst(e, e.target.value); }} 
                    />
                </div>
                <ScrollSync>
                    {({ onScroll, scrollTop }) => (
                        <div className="lst-list">
                            <div className="lst-left">
                                <div className="lst-header">
                                    <div className="lst-row fixed-column"><div className="cell cell-header">Value</div></div>
                                </div>
                                <div className="lst-side-body">
                                    <AutoSizer>
                                        {({ width, height }) => (
                                            <List
                                                key={`${selectedLstVersion}${selectedLst}${lstLocalVersion.current}`}
                                                ref={lstList}
                                                className="fixed-column"
                                                height={height}
                                                overscanRowCount={10}
                                                rowCount={lstView.current.length}
                                                rowHeight={35}
                                                rowRenderer={({ index, style }) => {
                                                    const item = lstView.current[index];
                                                    const key = item[dKey.KEY];
                                                    return (
                                                        <div 
                                                            key={`${key}${selectedLst}${selectedLstVersion}`} 
                                                            data-key={key} 
                                                            style={style} 
                                                            className="lst-row"
                                                        >
                                                            <div className="controls">
                                                                <DeleteIcon 
                                                                    className="delete" 
                                                                    onClick={onDeleteLstEntry} 
                                                                />
                                                            </div>
                                                            <div key={key + '$value'} className="cell">
                                                                <input 
                                                                    style={{ height: style.height }} 
                                                                    defaultValue={item.$value} 
                                                                    data-field="$value" 
                                                                    onChange={onLstCellChanged} 
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                                width={width}
                                                onScroll={onScroll}
                                                scrollTop={scrollTop}
                                            />
                                        )}
                                    </AutoSizer>
                                </div>
                            </div>
                            <div className="lst-right">
                                <div className="lst-right-wrapper">
                                    <div className="lst-header">
                                        {Array.from(columns.current, field => 
                                            <EditableHeader key={field} defaultValue={field} onChange={onLstFeatureChanged} />)}
                                    </div>
                                    <div className="lst-side-body">
                                        <AutoSizer>
                                            {({ width, height }) => (
                                                <List
                                                    key={`${selectedLstVersion}${selectedLst}${columns.current.size}${lstLocalVersion.current}`}
                                                    className="grid"
                                                    height={height}
                                                    overscanRowCount={10}
                                                    rowCount={lstView.current.length}
                                                    rowHeight={35}
                                                    rowRenderer={renderLstFeaturesRow}
                                                    width={width}
                                                    onScroll={onScroll}
                                                    scrollTop={scrollTop}
                                                />
                                            )}
                                        </AutoSizer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </ScrollSync>
            </React.Fragment>
        );
    }, [
        onAddFeature, 
        onAddLstEntry, 
        onSaveLst, 
        lstDuplicatesCount, 
        onDownloadLst, 
        onDedupLst, 
        onFilterLst, 
        selectedLstVersion, 
        selectedLst, 
        onDeleteLstEntry, 
        onLstCellChanged, 
        dKey.KEY, 
        onLstFeatureChanged, 
        renderLstFeaturesRow,
    ]);
    
    return (
        <div className="container-fluid gazetteer-editor">
            <div className="row">
                <div className="col-xs-6 def-file-entry">
                    <div>Gazetteer Index File</div>
                    {renderDefTable()}
                </div>
                <div className="col-xs-6 lst-file-entry">
                    <div>Gazetteer: {selectedLst}</div>
                    {renderLstTable()}
                </div>
            </div>
        </div>
    );
};

function EditableHeader(props) {
    const { onChange, defaultValue } = props;
    const session = useRef(Math.random().toFixed(6)).current;

    const onBlur = (e) => {
        if (e.relatedTarget && e.relatedTarget.dataset.session === session) return;
        onChange(defaultValue, e.target.value);
    };

    const onKeyUp = (e) => {
        if (e.key === 'Enter') {
            onChange(defaultValue, e.target.value);
            e.target.blur();
        }
    };

    const onRemoveClicked = () => {
        onChange(defaultValue, null, true);
    };

    return (
        <div key={defaultValue} className="cell cell-header editable">
            <input {...props} key={defaultValue + session} onKeyUp={onKeyUp} onBlur={onBlur} onChange={() => {}} />
            <div className="remove" tabIndex={0} data-session={session} onClick={onRemoveClicked}>&#x274C;</div>
        </div>
    );
}

export default withGazetterContentSave(GazetteerEditorPage);

function prettyPrint(gazetteersChanged) {
    console.group('%c Gazetteer Changes ', 'background: #222; color: #bada55; font-size: 14px; font-weight: bold; border-radius: 3px; padding: 2px;');
    gazetteersChanged.current.forEach((change) => {
        const {
            key, ...rest
        } = change;
        const statusStyle = `color: ${
            change.status === 'new' ? 'green' : change.status === 'remove' ? 'red' : 'orange'
        }; font-weight: bold;`;
        console.log(`%c[${change.status.toUpperCase()}]`, statusStyle, rest);
    });
    console.groupEnd();
}



// WEBPACK FOOTER //
// ./app/components/Gazetteers/Page.jsx